import { MessageCircle, Search, User, X } from 'lucide-react'
import Image from 'next/image'
import { useRouter } from 'next/router'
import * as React from 'react'

import { useDebouncedCallback } from '@/hooks/use-debounced-callback'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

import { useAuth } from '@/contexts/auth-context'
import { useChat } from '@/contexts/chat-context'

interface UnifiedSearchDialogProps {
  isOpen: boolean
  searchQuery: string
  searchResults: {
    users: Array<{
      id: number
      firstName: string
      lastName: string
      email?: string
      avatar?: string
      profileId?: string
    }>
    messages: Array<{
      id: number
      content: string
      sender: { id: number; firstName: string; lastName: string; avatar?: string }
      createdAt: string
      conversationId: number
    }>
    conversations: Array<{
      id: number
      name: string
      description?: string
      avatar?: string
      type: string
      lastMessageAt?: string
      lastMessageContent?: string
      participants: Array<{
        id: number
        firstName: string
        lastName: string
        email?: string
        avatar?: string
        profileId?: string
      }>
    }>
  }
  onClose: () => void
  onSearchQueryChange: (query: string) => void
  onUserSelect: (user: {
    id: number
    firstName: string
    lastName: string
    email: string
    avatar?: string
    profileId?: string
  }) => void
  onMessageSelect: (messageId: number, conversationId?: number) => void
  onConversationSelect: (conversationId: number) => void
  onSearch: () => void
  formatTimestamp: (timestamp: string) => string
}

export const UnifiedSearchDialog: React.FC<UnifiedSearchDialogProps> = ({
  isOpen,
  searchQuery,
  searchResults,
  onClose,
  onSearchQueryChange,
  onUserSelect: _onUserSelect,
  onMessageSelect,
  onConversationSelect,
  onSearch,
  formatTimestamp,
}) => {
  const { user } = useAuth()
  const { addParticipants, reloadConversations } = useChat()
  const router = useRouter()
  const [joiningConversationId, setJoiningConversationId] = React.useState<number | null>(null)
  const debouncedSearch = useDebouncedCallback(onSearch, 300)

  if (!isOpen) return null

  const handleJoinConversation = async (conversationId: number) => {
    if (!user?.id) return

    setJoiningConversationId(conversationId)
    try {
      await addParticipants(conversationId, [user.id])
      await reloadConversations()
      onClose()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to join conversation:', error)
    } finally {
      setJoiningConversationId(null)
    }
  }

  const isUserInConversation = (conversation: (typeof searchResults.conversations)[0]): boolean => {
    if (!user?.id) return false
    return conversation.participants.some(participant => participant.id === user.id)
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => {
          onSearchQueryChange('')
          onClose()
        }}
      />
      <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Search</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onSearchQueryChange('')
                onClose()
              }}
              className="p-1"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-4">
            <div className="space-y-3">
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search users, messages, and conversations..."
                  value={searchQuery}
                  onChange={e => {
                    onSearchQueryChange(e.target.value)
                    debouncedSearch()
                  }}
                  className="pl-10"
                  autoFocus
                />
                {searchQuery && (
                  <button
                    onClick={() => {
                      onSearchQueryChange('')
                    }}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Search Results */}
              {searchQuery.trim() && (
                <div className="max-h-96 overflow-y-auto space-y-3">
                  {/* Conversations Section */}
                  {searchResults.conversations.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Conversations ({searchResults.conversations.length})
                      </h4>
                      <div className="space-y-2">
                        {searchResults.conversations.map(conversation => (
                          <div
                            key={conversation.id}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <div
                              className="flex-1 flex items-center gap-3 cursor-pointer"
                              onClick={() => {
                                onClose()
                                onConversationSelect(conversation.id)
                              }}
                            >
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center overflow-hidden">
                                {conversation.avatar ? (
                                  <Image
                                    src={conversation.avatar}
                                    alt={conversation.name}
                                    width={32}
                                    height={32}
                                    className="w-8 h-8 rounded-full object-cover"
                                  />
                                ) : (
                                  <MessageCircle className="w-4 h-4 text-blue-600" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{conversation.name}</p>
                                <div className="flex items-center gap-2">
                                  <p className="text-xs text-gray-500 truncate">
                                    {conversation.participants.length} participants
                                  </p>
                                  {conversation.lastMessageContent && (
                                    <>
                                      <span className="text-xs text-gray-300">•</span>
                                      <p className="text-xs text-gray-500 truncate">
                                        {conversation.lastMessageContent}
                                      </p>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            {/* Join button for course-type conversations */}
                            {conversation.type === 'group' && !isUserInConversation(conversation) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={e => {
                                  e.stopPropagation()
                                  handleJoinConversation(conversation.id)
                                }}
                                disabled={joiningConversationId === conversation.id}
                                className="flex-shrink-0"
                              >
                                {joiningConversationId === conversation.id ? 'Joining...' : 'Join'}
                              </Button>
                            )}
                            {/* Show joined indicator for course-type conversations */}
                            {conversation.type === 'group' && isUserInConversation(conversation) && (
                              <span className="text-xs text-green-600 font-medium flex-shrink-0">Joined</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Users Section */}
                  {searchResults.users.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Users ({searchResults.users.length})</h4>
                      <div className="space-y-2">
                        {searchResults.users.map(user => (
                          <div
                            key={user.id}
                            onClick={() => {
                              onClose()
                              // Clear search state
                              onSearchQueryChange('')
                              // Navigate to user profile using profileId
                              if (user.profileId) {
                                router.push(`/user/${user.profileId}`)
                              } else {
                                // Only fallback to user ID if profileId is truly not available
                                router.push(`/user/${user.id}`)
                              }
                            }}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                          >
                            <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                              {user.avatar ? (
                                <Image
                                  src={user.avatar}
                                  alt={`${user.firstName} ${user.lastName}`}
                                  width={32}
                                  height={32}
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              ) : (
                                <User className="w-4 h-4 text-gray-600" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {user.firstName} {user.lastName}
                              </p>
                              <p className="text-xs text-gray-500 truncate">{user.email}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Messages Section */}
                  {searchResults.messages.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Messages ({searchResults.messages.length})
                      </h4>
                      <div className="space-y-2">
                        {searchResults.messages.map(message => (
                          <div
                            key={message.id}
                            onClick={() => {
                              onClose()
                              onMessageSelect(message.id, message.conversationId)
                            }}
                            className="p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <div className="w-4 h-4 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                                {message.sender.avatar ? (
                                  <Image
                                    src={message.sender.avatar}
                                    alt={`${message.sender.firstName} ${message.sender.lastName}`}
                                    width={16}
                                    height={16}
                                    className="w-4 h-4 rounded-full object-cover"
                                  />
                                ) : (
                                  <User className="w-2 h-2 text-gray-600" />
                                )}
                              </div>
                              <span className="text-xs font-medium text-gray-600">
                                {message.sender.firstName} {message.sender.lastName}
                              </span>
                              <span className="text-xs text-gray-400">{formatTimestamp(message.createdAt)}</span>
                            </div>
                            <p className="text-sm text-gray-900 line-clamp-2">{message.content}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* No Results */}
                  {searchResults.users.length === 0 &&
                    searchResults.messages.length === 0 &&
                    searchResults.conversations.length === 0 && (
                      <div className="text-center py-4">
                        <p className="text-sm text-gray-500">No results found for "{searchQuery}"</p>
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
